import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  DeviceEventEmitter,
  FlatList,
  Platform,
  Pressable,
  Text,
  View,
  useWindowDimensions,
} from 'react-native';
import { isTablet } from 'react-native-device-info';
import FastImage from '@d11/react-native-fast-image';
import { ActivityIndicator } from 'react-native-paper';
import {
  useFocusEffect,
  useNavigation,
  useRoute,
} from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { GTFonts, useLayoutSize, useTheme } from '../../theme';
import { fs, s, v, formatWithCommas, forceHttps } from '../../utils/libs';
import { shouldShowTabletSidebarLayoutSetting } from '../../utils/layoutUtils';
import AppSearchBar from '../../components/Reusables/AppSearchBar';
import AppSearchBarWeb from '../../components/Reusables/AppSearchBar.web';
import WebHeaderBar from '../../components/web/WebHeaderBar';
import useWebHeader from '../../components/web/useWebHeader';
import { useAppDispatch, useAppSelector } from '../../redux/helper';
import useBackofficeAxios from '../../hooks/useBackofficeAxios';
import useBackofficeOwnerAuth from '../../hooks/useBackofficeOwnerAuth';
import type { RootStackParamList } from '../../navigation/AppStack';
import {
  setProducts,
  setProductsError,
  setProductsLoading,
  setSelectedProductId,
  ensureProductsListInitialized,
  setTableSearchTerm,
  setTableSearchResults,
  clearTableSearch,
  type BackofficeProduct,
} from '../../redux/backoffice/productsSlice';
import {
  listOwnerProducts,
  searchOwnerProducts,
} from '../../api/backofficeProducts';
import { toFiniteNumber } from '../../utils/backofficeProductView';
import {
  DEFAULT_INVENTORY_FILTERS,
  DEFAULT_INVENTORY_SORT,
  buildInventoryListQuery,
  describeFilters,
  filtersFromRouteParams,
  getAppliedFilterChips,
  getSortLabel,
  getStockValueTitle,
  hasActiveFilters,
  parseStockStatusCounts,
  removeFilterChip,
  type AppliedFilterChip,
  type InventoryFilters,
  type InventorySort,
  type StockStatusCounts,
} from '../../utils/inventoryFilters';
import { useCurrencyText } from './productView/productViewFormat';
import { createInventoryStyles } from './inventory/inventoryStyles';
import InventoryHeaderSection from './inventory/InventoryHeaderSection';
import InventoryProductRow from './inventory/InventoryProductRow';
import InventoryFiltersSheet, {
  type DraftCounts,
} from './inventory/InventoryFiltersSheet';
import InventorySortSheet from './inventory/InventorySortSheet';
import { InventoryEmptyState } from './inventory/InventoryParts';
import { useStockValue } from './inventory/useStockValue';
import { useInventoryCategories } from './inventory/useInventoryCategories';

const SEARCH_DEBOUNCE_MS = 300;

const useDebouncedValue = <T,>(value: T, delay: number): T => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
};

type ListResponse = {
  docs: BackofficeProduct[];
  total: number;
  counts: StockStatusCounts | null;
};

/** Reads a list or search response; null for anything but an explicit success. */
const readListResponse = (res: any): ListResponse | null => {
  if (res?.status !== 200) return null;
  const body = res?.data;
  if (!body || typeof body !== 'object') return null;
  if (body.status != null && Number(body.status) !== 200) return null;
  const payload = body.data;
  if (!payload || typeof payload !== 'object') return null;
  const docs = Array.isArray(payload.data) ? payload.data : [];
  return {
    docs,
    total: toFiniteNumber(payload.total) ?? docs.length,
    counts: parseStockStatusCounts(payload),
  };
};

/**
 * The owner's Inventory: the stock value of the whole store or of the
 * filtered products, then the product list with
 * search, sort and filters (stock status, category, show only). Every filter
 * runs on the server, so large shops page over the filtered set.
 */
const BackOfficeProductsScreen: React.FC = () => {
  const { width, height } = useWindowDimensions();
  const nativeHeaderSearchWidth = isTablet()
    ? Math.max(width * 0.7, width - s(110))
    : width * 0.7;
  const { useSidebarNav } = useLayoutSize();
  const showTabletSidebarLayoutSetting = shouldShowTabletSidebarLayoutSetting(
    width,
    height,
  );
  const nav = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const route = useRoute<any>();
  const { theme } = useTheme();
  const styles = useMemo(() => createInventoryStyles(theme), [theme]);
  const currencyText = useCurrencyText();
  const isWeb = Platform.OS === 'web';
  // Two columns of rows on web, as before, once the window is wide enough for them
  const twoColumns = isWeb && width >= 700;

  const dispatch = useAppDispatch();
  const axiosBackoffice = useBackofficeAxios();
  const { owner } = useBackofficeOwnerAuth();
  const employee = useAppSelector(
    s => s.auth.inApp.account.employeeInfo as any,
  );
  const ownerId = String(employee?.bp_owner_id || '');
  const locationVersion = useAppSelector(s => s.location.locationVersion);

  // ---------- list state (Redux keeps the pages; filters live here) ----------
  const items = useAppSelector(s => s.backofficeProducts?.list?.items ?? []);
  const loading = useAppSelector(
    s => s.backofficeProducts?.list?.loading ?? false,
  );
  const error = useAppSelector(s => s.backofficeProducts?.list?.error ?? null);
  const page = useAppSelector(s => s.backofficeProducts?.list?.page ?? 1);
  const pageSize = useAppSelector(
    s => s.backofficeProducts?.list?.pageSize ?? 500,
  );
  const total = useAppSelector(s => s.backofficeProducts?.list?.total ?? 0);
  const tableSearchTerm = useAppSelector(
    s => s.backofficeProducts?.list?.tableSearchTerm ?? '',
  );
  const tableSearchItems = useAppSelector(
    s => s.backofficeProducts?.list?.tableSearchItems ?? [],
  );
  const tableSearchTotal = useAppSelector(
    s => s.backofficeProducts?.list?.tableSearchTotal ?? 0,
  );
  const tableSearchPage = useAppSelector(
    s => s.backofficeProducts?.list?.tableSearchPage ?? 1,
  );
  const tableSearchPageSize = useAppSelector(
    s => s.backofficeProducts?.list?.tableSearchPageSize ?? 100,
  );

  const [filters, setFilters] = useState<InventoryFilters>(
    DEFAULT_INVENTORY_FILTERS,
  );
  const [sort, setSort] = useState<InventorySort>(DEFAULT_INVENTORY_SORT);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [sortOpen, setSortOpen] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const loadMoreLockRef = useRef(false);
  const requestRef = useRef(0);
  const itemsRef = useRef(items);
  const searchItemsRef = useRef(tableSearchItems);
  itemsRef.current = items;
  searchItemsRef.current = tableSearchItems;

  const stockValue = useStockValue(filters);
  const {
    categories,
    failed: categoriesFailed,
    reload: reloadCategories,
  } = useInventoryCategories();

  const term = useDebouncedValue(
    String(tableSearchTerm || '').trim(),
    SEARCH_DEBOUNCE_MS,
  );
  const isSearch = term.length > 0;
  const activeItems = isSearch ? tableSearchItems : items;
  const effectivePage = isSearch ? tableSearchPage : page;
  const effectivePageSize = isSearch ? tableSearchPageSize : pageSize;
  const effectiveTotal = isSearch ? tableSearchTotal : total;
  const query = useMemo(
    () => buildInventoryListQuery(filters, sort),
    [filters, sort],
  );

  useEffect(() => {
    dispatch(ensureProductsListInitialized());
  }, [dispatch]);

  // ---------- header ----------
  const headerRightNode = useMemo(
    () => (
      <View style={localStyles.headerRight}>
        <Pressable
          onPress={() => nav.navigate('BackOfficeAddProduct')}
          style={({ pressed, hovered }: any) => [
            localStyles.addProductBtn,
            pressed && localStyles.addProductBtnPressed,
            isWeb && hovered && localStyles.addProductBtnHovered,
          ]}>
          <Text style={localStyles.addProductBtnText}>+ Add New</Text>
        </Pressable>
      </View>
    ),
    [isWeb, nav],
  );

  // Scanning from here must not fill the product form, and a scan started
  // from the product form must not change this search.
  const awaitingScanRef = useRef(false);
  const openScanner = useCallback(() => {
    awaitingScanRef.current = true;
    nav.navigate('BarcodeCapture', { skipProductForm: true });
  }, [nav]);
  useEffect(() => {
    const subscription = DeviceEventEmitter.addListener(
      'BARCODE_SCANNED',
      (code: unknown) => {
        if (!awaitingScanRef.current) return;
        awaitingScanRef.current = false;
        const text = String(code ?? '').trim();
        if (text) dispatch(setTableSearchTerm(text));
      },
    );
    return () => subscription.remove();
  }, [dispatch]);
  useFocusEffect(
    useCallback(() => {
      // Back without a scan: forget the pending scan after the event window
      const timer = setTimeout(() => {
        awaitingScanRef.current = false;
      }, 500);
      return () => clearTimeout(timer);
    }, []),
  );

  const searchBar = useMemo(
    () => (
      <View style={{ width: '100%', maxWidth: nativeHeaderSearchWidth }}>
        <AppSearchBar
          placeholder="Search products"
          value={tableSearchTerm}
          onChangeText={text => dispatch(setTableSearchTerm(text))}
          onClear={() => dispatch(clearTableSearch())}
          showLeadingIcon={true}
          // The clear button takes the scan button's place while there is text
          showBarcodeScan={!String(tableSearchTerm || '').trim()}
          onBarcodeScan={openScanner}
          width={nativeHeaderSearchWidth}
          containerStyle={{
            backgroundColor: theme.colors.surface,
            borderColor: theme.colors.surface,
            borderWidth: 0,
          }}
        />
      </View>
    ),
    [dispatch, nativeHeaderSearchWidth, openScanner, tableSearchTerm, theme],
  );

  const webSearchBar = useMemo(
    () => (
      <View
        style={{
          width: '100%',
          maxWidth: width >= 768 && width < 1024 ? s(660) : s(620),
        }}>
        <AppSearchBarWeb
          placeholder="Search products"
          value={tableSearchTerm}
          onChangeText={text => dispatch(setTableSearchTerm(text))}
          onClear={() => dispatch(clearTableSearch())}
          showLeadingIcon={true}
          showBarcodeScan={false}
          width={
            showTabletSidebarLayoutSetting && useSidebarNav
              ? width * 0.46
              : width * 0.32
          }
          height={v(50)}
          containerStyle={{
            backgroundColor: theme.colors.surface,
            borderColor: theme.colors.surface,
            borderWidth: 0,
          }}
        />
      </View>
    ),
    [
      dispatch,
      showTabletSidebarLayoutSetting,
      tableSearchTerm,
      theme,
      useSidebarNav,
      width,
    ],
  );

  useLayoutEffect(() => {
    if (useSidebarNav) return;
    (nav as any).setOptions({
      headerTitleContainerStyle: { flex: 1 },
      headerTitle: () => searchBar,
      headerRight: () => headerRightNode,
    });
  }, [headerRightNode, nav, searchBar, useSidebarNav]);

  const webHeaderNode = useMemo(() => {
    if (!useSidebarNav) return null;
    return (
      <WebHeaderBar
        rightSlot={headerRightNode}
        onBack={() => {
          try {
            (nav as any).navigate('BackOffice', { screen: 'Dashboard' });
          } catch {
            if (nav.canGoBack()) nav.goBack();
          }
        }}>
        {webSearchBar}
      </WebHeaderBar>
    );
  }, [headerRightNode, nav, useSidebarNav, webSearchBar]);
  useWebHeader(webHeaderNode, Boolean(webHeaderNode));

  // ---------- data ----------
  const fetchPage = useCallback(
    async (
      pageToLoad: number,
      options: { append: boolean; silent?: boolean },
    ) => {
      if (!owner?.accessToken || !ownerId) return;
      const requestId = ++requestRef.current;
      const auth = { accessToken: owner.accessToken };
      if (!options.append && !options.silent)
        dispatch(setProductsLoading(true));
      dispatch(setProductsError(null));
      try {
        const res = isSearch
          ? await searchOwnerProducts(
              axiosBackoffice as any,
              term,
              pageToLoad,
              tableSearchPageSize,
              auth,
              query,
            )
          : await listOwnerProducts(
              axiosBackoffice as any,
              ownerId,
              pageToLoad,
              pageSize,
              auth,
              query,
            );
        if (requestId !== requestRef.current) return;
        const parsed = readListResponse(res);
        if (!parsed) {
          dispatch(
            setProductsError(
              String(res?.data?.message || 'Failed to load products'),
            ),
          );
          return;
        }
        if (isSearch) {
          dispatch(
            setTableSearchResults({
              items: options.append
                ? [...searchItemsRef.current, ...parsed.docs]
                : parsed.docs,
              total: parsed.total,
              page: pageToLoad,
              pageSize: tableSearchPageSize,
            }),
          );
        } else {
          dispatch(
            setProducts({
              items: options.append
                ? [...itemsRef.current, ...parsed.docs]
                : parsed.docs,
              total: parsed.total,
              page: pageToLoad,
              pageSize,
            }),
          );
        }
      } catch (e: any) {
        if (requestId !== requestRef.current) return;
        dispatch(setProductsError(e?.message || 'Error fetching products'));
      } finally {
        if (requestId === requestRef.current) {
          dispatch(setProductsLoading(false));
        }
      }
    },
    [
      axiosBackoffice,
      dispatch,
      isSearch,
      owner?.accessToken,
      ownerId,
      pageSize,
      query,
      tableSearchPageSize,
      term,
    ],
  );

  // First page whenever the search, filters, sort or location change
  useEffect(() => {
    loadMoreLockRef.current = false;
    void fetchPage(1, { append: false });
  }, [fetchPage, locationVersion]);

  // The Summary cards and the Stock value screen open the list already filtered
  const presetKey = `${route?.params?.preset ?? ''}|${route?.params?.category ?? ''}|${route?.params?.showOnly ?? ''}|${JSON.stringify(route?.params?.filters ?? null)}`;
  useEffect(() => {
    const preset = filtersFromRouteParams(route?.params);
    if (!preset) return;
    setFilters(preset);
    dispatch(clearTableSearch());
    (nav as any).setParams({
      preset: undefined,
      category: undefined,
      showOnly: undefined,
      filters: undefined,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presetKey]);

  const hasNextPage = useMemo(() => {
    const size = Math.max(1, Number(effectivePageSize || 1));
    const maxPages = Math.max(1, Math.ceil(Number(effectiveTotal || 0) / size));
    return Number(effectivePage || 1) < maxPages;
  }, [effectiveTotal, effectivePageSize, effectivePage]);

  const loadMore = useCallback(() => {
    if (loadMoreLockRef.current || isLoadingMore || loading || !hasNextPage)
      return;
    loadMoreLockRef.current = true;
    setIsLoadingMore(true);
    void fetchPage(Number(effectivePage || 1) + 1, { append: true }).finally(
      () => {
        setIsLoadingMore(false);
        loadMoreLockRef.current = false;
      },
    );
  }, [effectivePage, fetchPage, hasNextPage, isLoadingMore, loading]);

  const onRefresh = useCallback(async () => {
    if (refreshing) return;
    setRefreshing(true);
    loadMoreLockRef.current = false;
    try {
      await Promise.all([
        fetchPage(1, { append: false, silent: true }),
        stockValue.reload(),
        reloadCategories(),
      ]);
    } finally {
      setRefreshing(false);
    }
  }, [fetchPage, refreshing, reloadCategories, stockValue]);

  /** Counts for a draft of the filters, for the sheet's chips and its button. */
  const fetchDraftCounts = useCallback(
    async (draft: InventoryFilters): Promise<DraftCounts | null> => {
      if (!owner?.accessToken || !ownerId) return null;
      const auth = { accessToken: owner.accessToken };
      // The only request that asks for per-status counts
      const draftQuery = buildInventoryListQuery(draft, sort, {
        includeCounts: true,
      });
      try {
        const res = isSearch
          ? await searchOwnerProducts(
              axiosBackoffice as any,
              term,
              1,
              1,
              auth,
              draftQuery,
            )
          : await listOwnerProducts(
              axiosBackoffice as any,
              ownerId,
              1,
              1,
              auth,
              draftQuery,
            );
        const parsed = readListResponse(res);
        return parsed ? { counts: parsed.counts, total: parsed.total } : null;
      } catch {
        return null;
      }
    },
    [axiosBackoffice, isSearch, owner?.accessToken, ownerId, sort, term],
  );

  // ---------- derived ----------
  const chips = useMemo(() => getAppliedFilterChips(filters), [filters]);
  const filtersActive = hasActiveFilters(filters);
  const listEmpty = activeItems.length === 0;
  const showPlaceholderCount = listEmpty && (Boolean(error) || loading);
  const countText = showPlaceholderCount
    ? '— products'
    : `${formatWithCommas(Number(effectiveTotal || 0), 0)} ${
        Number(effectiveTotal) === 1 ? 'product' : 'products'
      }`;

  const onRemoveChip = useCallback(
    (chip: AppliedFilterChip) =>
      setFilters(current => removeFilterChip(current, chip)),
    [],
  );
  const clearFilters = useCallback(
    () => setFilters(DEFAULT_INVENTORY_FILTERS),
    [],
  );

  const onPressItem = useCallback(
    (product: BackofficeProduct) => {
      if (!product) return;
      dispatch(setSelectedProductId(product._id));
      nav.navigate('BackOfficeProductView', { productId: product._id });
    },
    [dispatch, nav],
  );

  const renderItem = useCallback(
    ({ item, index }: { item: BackofficeProduct; index: number }) => (
      <InventoryProductRow
        styles={styles}
        theme={theme}
        product={item}
        index={index}
        currencyText={currencyText}
        onPress={onPressItem}
        inGrid={twoColumns}
      />
    ),
    [currencyText, onPressItem, styles, theme, twoColumns],
  );

  // Preload visible and upcoming images for smoother scrolling
  const viewabilityConfig = useRef({ itemVisiblePercentThreshold: 50 }).current;
  const activeItemsRef = useRef(activeItems);
  activeItemsRef.current = activeItems;
  // FlatList does not support changing this callback after mount: keep the
  // identity stable and read the latest data from a ref.
  const onViewableItemsChanged = useRef(({ viewableItems }: any) => {
    try {
      if (!Array.isArray(viewableItems) || viewableItems.length === 0) return;
      const data = activeItemsRef.current ?? [];
      const start = Math.max(0, viewableItems[0]?.index ?? 0);
      const sources = data
        .slice(start, Math.min(data.length, start + 20))
        .map((p: any) => String(p?.bp_product_image || ''))
        .filter(Boolean)
        .map(uri => ({
          uri: forceHttps(uri),
          priority: FastImage.priority.normal,
        }));
      if (sources.length) FastImage.preload(sources as any);
    } catch {}
  }).current;

  // ---------- empty states ----------
  const emptyNode = useMemo(() => {
    if (error && listEmpty) {
      return (
        <InventoryEmptyState
          styles={styles}
          theme={theme}
          icon="alert-circle-outline"
          title="Couldn’t load products"
          message="Check your connection and try again."
          primary={{
            label: 'Try again',
            onPress: () => void fetchPage(1, { append: false }),
          }}
        />
      );
    }
    if (loading && listEmpty) {
      return (
        <View style={styles.empty}>
          <ActivityIndicator />
          <Text style={styles.emptyText}>Loading products…</Text>
        </View>
      );
    }
    if (isSearch) {
      return (
        <InventoryEmptyState
          styles={styles}
          theme={theme}
          icon="magnify-close"
          title={`No products match “${term}”`}
          message={
            filtersActive
              ? `Nothing matches “${term}” with ${describeFilters(filters)}.`
              : 'Check the spelling, or add it as a new product.'
          }
          primary={{
            label: `Add “${term}”`,
            icon: 'plus',
            onPress: () => nav.navigate('BackOfficeAddProduct'),
          }}
          primaryColor={theme.colors.success}
          secondary={
            filtersActive
              ? { label: 'Clear filters', onPress: clearFilters }
              : undefined
          }
        />
      );
    }
    if (filtersActive) {
      const statusOnly = chips.length === 1 && chips[0].kind === 'status';
      if (statusOnly) {
        const out = filters.stockStatus === 'out';
        return (
          <InventoryEmptyState
            styles={styles}
            theme={theme}
            icon="check-circle-outline"
            title={
              out
                ? 'No products are out of stock'
                : 'No products are low on stock'
            }
            message={
              out
                ? 'Every product has stock.'
                : 'Every product is above its minimum stock level.'
            }
            ghost
            primary={{ label: 'Show all products', onPress: clearFilters }}
          />
        );
      }
      return (
        <InventoryEmptyState
          styles={styles}
          theme={theme}
          icon="filter-off-outline"
          title="No products match"
          message={`Nothing matches ${describeFilters(filters)}.`}
          ghost
          primary={{ label: 'Clear filters', onPress: clearFilters }}
        />
      );
    }
    return (
      <InventoryEmptyState
        styles={styles}
        theme={theme}
        icon="package-variant"
        title="No products yet"
        message="Add products to get started."
        primary={{
          label: 'Add Product',
          icon: 'plus',
          onPress: () => nav.navigate('BackOfficeAddProduct'),
        }}
        primaryColor={theme.colors.success}
      />
    );
  }, [
    chips,
    clearFilters,
    error,
    fetchPage,
    filters,
    filtersActive,
    isSearch,
    listEmpty,
    loading,
    nav,
    styles,
    term,
    theme,
  ]);

  const tableHeadColumns = (
    <>
      <Text
        style={[
          styles.listHeadText,
          styles.columnIndex,
          twoColumns && styles.columnIndexInGrid,
        ]}>
        #
      </Text>
      <Text style={[styles.listHeadText, styles.columnProduct]}>
        Product Name
      </Text>
      <Text style={[styles.listHeadText, styles.columnRight]}>
        Stock · Selling price
      </Text>
    </>
  );

  return (
    <View style={styles.screen}>
      <View style={styles.container}>
        <InventoryHeaderSection
          styles={styles}
          theme={theme}
          currencyText={currencyText}
          stockValue={stockValue}
          stockValueTitle={getStockValueTitle(filters)}
          onOpenStockValue={() =>
            nav.navigate(
              'BackOfficeStockValue',
              filtersActive ? { filters } : undefined,
            )
          }
          countText={countText}
          sortLabel={getSortLabel(sort)}
          onOpenSort={() => setSortOpen(true)}
          filterCount={chips.length}
          onOpenFilters={() => setFiltersOpen(true)}
          chips={chips}
          onRemoveChip={onRemoveChip}
          onClearFilters={clearFilters}
          wide={twoColumns}
        />

        <View style={styles.card}>
          <View style={styles.cardClip}>
            <FlatList
              data={activeItems}
              keyExtractor={item => String(item?._id)}
              renderItem={renderItem}
              numColumns={twoColumns ? 2 : 1}
              key={twoColumns ? 'web-2col' : 'one-col'}
              columnWrapperStyle={twoColumns ? styles.columnWrapper : undefined}
              {...(!isWeb
                ? {
                    maintainVisibleContentPosition: {
                      minIndexForVisible: 0,
                      autoscrollToTopThreshold: 10,
                    },
                  }
                : {})}
              onEndReached={loadMore}
              onEndReachedThreshold={0.5}
              refreshing={refreshing}
              onRefresh={onRefresh}
              onViewableItemsChanged={onViewableItemsChanged}
              viewabilityConfig={viewabilityConfig}
              stickyHeaderIndices={[0]}
              contentContainerStyle={
                listEmpty ? localStyles.emptyContent : undefined
              }
              ListHeaderComponent={
                twoColumns ? (
                  <View style={[styles.listHead, styles.listHeadGrid]}>
                    <View
                      style={[styles.listHeadHalf, styles.listHeadHalfDivider]}>
                      {tableHeadColumns}
                    </View>
                    <View style={styles.listHeadHalf}>{tableHeadColumns}</View>
                  </View>
                ) : (
                  <View style={styles.listHead}>{tableHeadColumns}</View>
                )
              }
              ListEmptyComponent={emptyNode}
              ListFooterComponent={
                isLoadingMore ? (
                  <View style={styles.footerLoading}>
                    <ActivityIndicator />
                  </View>
                ) : null
              }
            />
          </View>
        </View>
      </View>

      <InventoryFiltersSheet
        styles={styles}
        theme={theme}
        open={filtersOpen}
        filters={filters}
        total={showPlaceholderCount ? null : Number(effectiveTotal || 0)}
        categories={categories}
        categoriesFailed={categoriesFailed}
        onRetryCategories={() => void reloadCategories()}
        fetchDraftCounts={fetchDraftCounts}
        onApply={setFilters}
        onClose={() => setFiltersOpen(false)}
      />
      <InventorySortSheet
        styles={styles}
        theme={theme}
        open={sortOpen}
        sort={sort}
        onSelect={setSort}
        onClose={() => setSortOpen(false)}
      />
    </View>
  );
};

export default BackOfficeProductsScreen;

const localStyles = {
  headerRight: {
    flexDirection: 'row' as const,
    justifyContent: 'center' as const,
    alignItems: 'center' as const,
    marginRight: s(8),
  },
  addProductBtn: {
    marginLeft: Platform.OS === 'web' ? s(8) : 0,
    paddingHorizontal: s(10),
    paddingVertical: s(6),
    borderRadius: 8,
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
      transition: 'background-color 0.15s ease-out',
    }),
  } as any,
  addProductBtnHovered: {
    backgroundColor: 'rgba(255, 255, 255, 0.14)',
  } as any,
  addProductBtnPressed: {
    opacity: 0.75,
  },
  addProductBtnText: {
    color: '#FFFFFF',
    fontFamily: GTFonts.semibold,
    fontSize: fs(15),
    lineHeight: fs(16),
  },
  emptyContent: {
    flexGrow: 1,
  },
};
